import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp, Users, Check, Trash2, GripVertical } from 'lucide-react';
import DraggableApprovalLine from './DraggableApprovalLine';
import ApprovalSaveModal from './ApprovalSaveModal';

// 아코디언 회사 컴포넌트
const AccordionCompany = ({ companyName, userCount, children, onAddAll, isOpen, onToggle }) => {
  return (
    <div className="mb-2 rounded-xl border border-gray-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ChevronDown 
            size={16} 
            className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
          <span className="text-sm font-medium text-gray-800">{companyName}</span>
          <span className="px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 text-xs">{userCount}명</span>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAddAll();
          }}
          className="px-3 py-1.5 rounded-lg bg-blue-500 text-white text-xs font-medium hover:bg-blue-600 transition-colors"
        >
          전체추가
        </button>
      </button>
      {isOpen && (
        <div className="p-3 space-y-2 bg-white">
          {children}
        </div>
      )}
    </div>
  );
};

// 부서 블록 컴포넌트
const DepartmentBlock = ({ dept, users, selectedIds, onToggle, onAddAll }) => {
  return (
    <div className="pl-2">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-600">{dept}</span>
        <button
          type="button"
          onClick={() => onAddAll(users.map(u => u.id))}
          className="px-2 py-1 rounded-md bg-gray-100 text-gray-600 text-xs hover:bg-gray-200 transition-colors"
        >
          전체추가
        </button>
      </div>
      <div className="space-y-1">
        {users.map((user) => {
          const selected = selectedIds.includes(user.id);
          return (
            <button
              key={user.id}
              type="button"
              onClick={() => onToggle(user.id, user)}
              disabled={selected}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                selected
                  ? 'bg-blue-50 text-blue-400 cursor-not-allowed'
                  : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">{user.last_name}{user.first_name}</span>
                  <span className="ml-2 text-xs text-gray-500">{user.position}</span>
                </div>
                {selected && <Check size={16} className="text-blue-500" />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// 결재자 카드 컴포넌트 (모바일용)
const ApproverCard = ({ line, order, isParallel, onRemove, onCategoryChange }) => {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-200">
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white text-sm font-bold">
        {order}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">
          {line.approver_name || '미선택'}
        </div>
        <div className="text-xs text-gray-500">
          {line.position} {isParallel && '· 병렬결재'}
        </div>
      </div>
      <select
        value={line.approval_category || 'approval'}
        onChange={(e) => onCategoryChange(line.id, e.target.value)}
        className="px-2 py-1 rounded-lg bg-gray-100 text-xs text-gray-700 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="approval">결재</option>
        <option value="pre_approval">전결</option>
      </select>
      <button
        type="button"
        onClick={() => onRemove(line.id)}
        className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
};

const ApprovalLineModal = ({
  isOpen,
  onClose,
  users,
  approvalLines,
  onAddApprover,
  onRemoveApprover,
  onMoveApprover,
  onMoveApproverForReorder,
  onApproverChange,
  onApprovalTypeChange,
  onApprovalCategoryChange,
  onLoadTemplate,
  onSaveTemplate,
  onDeleteline,
  lines
}) => {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [activeTab, setActiveTab] = useState('users'); // 'users' | 'approvers' (모바일 탭)
  const [openCompanies, setOpenCompanies] = useState({});

  if (!isOpen) return null;

  // 회사 → 부서 → 사용자 그룹핑
  const grouped = {};
  users.forEach((u) => {
    const c = u.company || "미지정 회사";
    const d = u.department || "부서 없음";
    if (!grouped[c]) grouped[c] = {};
    if (!grouped[c][d]) grouped[c][d] = [];
    grouped[c][d].push(u);
  });

  const toggleCompany = (company) => {
    setOpenCompanies(prev => ({ ...prev, [company]: !prev[company] }));
  };

  const handleAddApprover = (userId, user) => {
    const exists = approvalLines.some((l) => l.approver_id === userId);
    if (exists) return;

    const nextOrder = approvalLines.length > 0
      ? Math.max(...approvalLines.map((l) => l.order)) + 1
      : 1;

    onAddApprover({
      id: Date.now(),
      approver_id: userId,
      approver_name: user.last_name + user.first_name,
      position: user.position,
      company: user.company,
      order: nextOrder,
      approval_type: "sequential",
      approval_category: "approval",
    });
  };

  const handleAddAllFromCompany = (company) => {
    const depts = grouped[company];
    Object.values(depts).flat().forEach(user => {
      handleAddApprover(user.id, user);
    });
  };

  // order 기준으로 그룹화
  const orderGroups = {};
  approvalLines.forEach((line) => {
    if (!orderGroups[line.order]) orderGroups[line.order] = [];
    orderGroups[line.order].push(line);
  });
  const sortedOrders = Object.keys(orderGroups).map(Number).sort((a, b) => a - b);

  return (
    <>
      {/* 배경 오버레이 */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" onClick={onClose} />
      
      {/* 모달 컨테이너 */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] max-h-[700px] flex flex-col overflow-hidden pointer-events-auto">
          
          {/* 헤더 */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-bold text-gray-900">결재선 관리</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-200 transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          {/* 모바일 탭 (md 이하에서만 표시) */}
          <div className="flex md:hidden border-b border-gray-200">
            <button
              type="button"
              onClick={() => setActiveTab('users')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'users'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500'
              }`}
            >
              사용자 목록
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('approvers')}
              className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
                activeTab === 'approvers'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500'
              }`}
            >
              결재선 구성
              {approvalLines.length > 0 && (
                <span className="absolute top-2 right-4 px-1.5 py-0.5 rounded-full bg-blue-500 text-white text-xs">
                  {approvalLines.length}
                </span>
              )}
            </button>
          </div>

          {/* 본문 */}
          <div className="flex flex-1 overflow-hidden">
            
            {/* 왼쪽: 사용자 목록 */}
            <div className={`w-full md:w-1/2 bg-gray-50 overflow-y-auto border-r border-gray-200 ${
              activeTab !== 'users' ? 'hidden md:block' : ''
            }`}>
              <div className="p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Users size={16} />
                  사용자 목록
                </h3>

                {/* 회사별 아코디언 */}
                {Object.entries(grouped).map(([company, depts]) => (
                  <AccordionCompany
                    key={company}
                    companyName={company}
                    userCount={Object.values(depts).flat().length}
                    isOpen={openCompanies[company]}
                    onToggle={() => toggleCompany(company)}
                    onAddAll={() => handleAddAllFromCompany(company)}
                  >
                    {Object.entries(depts).map(([dept, deptUsers]) => (
                      <DepartmentBlock
                        key={dept}
                        dept={dept}
                        users={deptUsers}
                        selectedIds={approvalLines.map((l) => l.approver_id)}
                        onToggle={handleAddApprover}
                        onAddAll={(ids) => {
                          ids.forEach(id => {
                            const user = deptUsers.find(u => u.id === id);
                            if (user) handleAddApprover(id, user);
                          });
                        }}
                      />
                    ))}
                  </AccordionCompany>
                ))}

                {/* 자주 쓰는 결재선 */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">자주 쓰는 결재선</h3>
                  {lines?.length > 0 ? (
                    <div className="space-y-2">
                      {lines.map(line => (
                        <div
                          key={line.id}
                          className="group relative p-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => {
                            const normalized = Array.isArray(line.approvers)
                              ? line.approvers
                              : Array.isArray(line.approvalLines)
                                ? line.approvalLines
                                : [];
                            onLoadTemplate(
                              normalized.map((a) => ({
                                id: a.id || Date.now() + Math.random(),
                                approver_id: a.approver_id,
                                approver_name: a.approver_name,
                                position: a.position,
                                company: a.company,
                                order: a.order,
                                approval_category: a.approval_category || "approval",
                                approval_type: a.approval_type || "sequential",
                              }))
                            );
                          }}
                        >
                          <span className="text-sm text-gray-700 font-medium">
                            {line.name || line.custom_line_name}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm("삭제하시겠습니까?")) {
                                onDeleteline(line.id);
                              }
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-100 text-red-500 transition-opacity"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 rounded-xl bg-gray-100 text-center">
                      <p className="text-sm text-gray-500">저장된 결재선이 없습니다.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 오른쪽: 결재선 구성 */}
            <div className={`w-full md:w-1/2 bg-white overflow-y-auto ${
              activeTab !== 'approvers' ? 'hidden md:block' : ''
            }`}>
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-700">
                    결재선 구성 ({approvalLines.length}명)
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowSaveModal(true)}
                    disabled={approvalLines.length === 0}
                    className="px-3 py-1.5 rounded-lg bg-blue-500 text-white text-xs font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    결재선 저장
                  </button>
                </div>

                {approvalLines.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                      <Users size={24} className="text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">왼쪽 목록에서<br/>결재자를 선택해주세요.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {sortedOrders.map((order, orderIdx) => (
                      <div key={order} className="space-y-2">
                        {/* 순서 이동 버튼 */}
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (orderIdx === 0) return;
                              const prevOrder = sortedOrders[orderIdx - 1];
                              const fromIndex = approvalLines.findIndex(l => l.order === order);
                              const toIndex = approvalLines.findIndex(l => l.order === prevOrder);
                              if (fromIndex !== -1 && toIndex !== -1) {
                                onMoveApproverForReorder(fromIndex, toIndex);
                              }
                            }}
                            disabled={orderIdx === 0}
                            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronUp size={16} className="text-gray-500" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (orderIdx === sortedOrders.length - 1) return;
                              const nextOrder = sortedOrders[orderIdx + 1];
                              const fromIndex = approvalLines.findIndex(l => l.order === order);
                              const toIndex = approvalLines.findIndex(l => l.order === nextOrder);
                              if (fromIndex !== -1 && toIndex !== -1) {
                                onMoveApproverForReorder(fromIndex, toIndex);
                              }
                            }}
                            disabled={orderIdx === sortedOrders.length - 1}
                            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronDown size={16} className="text-gray-500" />
                          </button>
                          <span className="text-xs text-gray-500">순서 {order}</span>
                        </div>
                        
                        {/* 결재자 카드들 */}
                        {orderGroups[order].map((line) => (
                          <ApproverCard
                            key={line.id}
                            line={line}
                            order={order}
                            isParallel={orderGroups[order].length > 1}
                            onRemove={onRemoveApprover}
                            onCategoryChange={onApprovalCategoryChange}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 푸터 */}
          <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              닫기
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl bg-blue-500 text-sm font-medium text-white hover:bg-blue-600 transition-colors"
            >
              확인
            </button>
          </div>
        </div>
      </div>

      <ApprovalSaveModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={onSaveTemplate}
        approvalLines={approvalLines}
        users={users}
      />
    </>
  );
};

export default ApprovalLineModal;
